import type { UserProfile } from '../types';

export const METRONOME_BPM_MIN = 50;
export const METRONOME_BPM_MAX = 120;

const CURRICULUM_TIER_ORDER = [
  'niveau-1',
  'niveau-2',
  'niveau-3',
  'niveau-4',
] as const;

/** BPM after completing niveau-1 … niveau-4 (highest cleared tier wins). */
const METRONOME_BPM_BY_TIER = [55, 60, 65, 70] as const;

/**
 * Shadow scrolls slightly below metronome pace (one char per beat × headroom).
 * Metronome at 60 BPM ≈ 1 char/s; old 3–4.5 cps was far too fast.
 */
const SHADOW_CPS_HEADROOM = 0.85;

/** Index of the highest completed curriculum tier, or -1 if none. */
export function highestCompletedCurriculumTierIndex(
  user: UserProfile,
): number {
  let best = -1;
  for (let i = 0; i < CURRICULUM_TIER_ORDER.length; i++) {
    if (user.completedTierIds.includes(CURRICULUM_TIER_ORDER[i])) {
      best = i;
    }
  }
  return best;
}

export function clampMetronomeBpm(bpm: number): number {
  if (!Number.isFinite(bpm)) return METRONOME_BPM_MIN;
  return Math.round(
    Math.max(METRONOME_BPM_MIN, Math.min(METRONOME_BPM_MAX, bpm)),
  );
}

/** Progression tempo from completed curriculum tiers (55 → 70). */
export function suggestedMetronomeBpmForUser(user: UserProfile): number {
  const idx = highestCompletedCurriculumTierIndex(user);
  if (idx < 0) return METRONOME_BPM_BY_TIER[0];
  return (
    METRONOME_BPM_BY_TIER[idx] ??
    METRONOME_BPM_BY_TIER[METRONOME_BPM_BY_TIER.length - 1]
  );
}

/** @deprecated Use suggestedMetronomeBpmForUser — kept for call-site clarity. */
export const metronomeBpmForUser = suggestedMetronomeBpmForUser;

export function isMetronomeBpmCustom(user: UserProfile): boolean {
  return typeof user.preferences?.metronomeBpm === 'number';
}

/** Saved custom BPM, or suggested when none saved. */
export function effectiveMetronomeBpmForUser(user: UserProfile): number {
  const custom = user.preferences?.metronomeBpm;
  if (typeof custom === 'number' && Number.isFinite(custom)) {
    return clampMetronomeBpm(custom);
  }
  return suggestedMetronomeBpmForUser(user);
}

export function shadowCpsForUser(user: UserProfile): number {
  const bpm = effectiveMetronomeBpmForUser(user);
  const raw = (bpm / 60) * SHADOW_CPS_HEADROOM;
  return Math.max(0.75, Math.round(raw * 10) / 10);
}

const FLASH_MS_MIN = 500;
const FLASH_MS_PER_CHAR = 200;
const FLASH_MS_BASE_OFFSET = 350;

/** Shorter flash for short words (e.g. 2-bogstavs ord ~750 ms ved base 1500). */
export function flashDurationForWord(
  baseMs: number,
  wordLength: number,
): number {
  const scaled = FLASH_MS_BASE_OFFSET + wordLength * FLASH_MS_PER_CHAR;
  return Math.min(baseMs, Math.max(FLASH_MS_MIN, scaled));
}
